import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { GraphQLErrorMessage, Paragraph, Switch, useToasts } from '@treams/treams-ui';

export type KeyPositionJobTitle = Pick<
	graphql.JobTitle,
	'id' | 'isKeyPosition' | 'keyPositionMarkedAt' | 'viewerCanManageSuccession'
> & {
	keyPositionMarkedBy?: Pick<graphql.User, 'id' | 'firstName' | 'lastName'> | null;
};

// Sleutelrol switch. Only rendered when the succession feature is on and the
// viewer may manage succession for this title. Shows who marked it and when.
export function KeyPositionToggle({ jobTitle }: { jobTitle: KeyPositionJobTitle }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const enabled = useFeature(graphql.FeatureName.Succession);
	const [setKeyPosition, { loading, error }] = graphql.useSetJobTitleKeyPositionMutation();

	if (!enabled || !jobTitle.viewerCanManageSuccession) {
		return null;
	}

	const handleChange = async (on: boolean) => {
		try {
			await setKeyPosition({ variables: { input: { id: jobTitle.id, isKeyPosition: on } } });
			addToast(t(on ? 'jobPosition.keyPosition.marked' : 'jobPosition.keyPosition.unmarked'), {
				appearance: 'success',
				autoDismiss: true,
			});
		} catch {
			// error state is shown via GraphQLErrorMessage
		}
	};

	return (
		<Box>
			<Switch
				label={t('jobPosition.keyPosition.label')}
				on={jobTitle.isKeyPosition}
				disabled={loading}
				onChange={handleChange}
			/>
			{jobTitle.isKeyPosition && jobTitle.keyPositionMarkedBy && (
				<Paragraph color="slateBlue" mt={1}>
					{t('jobPosition.keyPosition.markedBy', {
						name: `${jobTitle.keyPositionMarkedBy.firstName} ${jobTitle.keyPositionMarkedBy.lastName}`,
						date: jobTitle.keyPositionMarkedAt
							? moment(jobTitle.keyPositionMarkedAt).format('LL')
							: '',
					})}
				</Paragraph>
			)}
			{error?.graphQLErrors[0] && (
				<Box mt={2}>
					<GraphQLErrorMessage error={error.graphQLErrors[0]} />
				</Box>
			)}
		</Box>
	);
}
